import { useRef, useState } from "react";
import "./index.css";
import { Flavor, SOLDOUT_TYPE } from "./MenuFlavors/types";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { InstagramEmbed } from "react-social-media-embed";
import { SocialLogo } from "social-logos";

interface MenuProps {
  headerText: string;
  otherInformation?: {
    eventDate: string;
    eventLocation: string;
  };
  flavors: Flavor[];
  menuBackgroundColorClass?: string;
  shouldShowTastingNotesHeader?: boolean;
}

export default function Menu({
  headerText,
  flavors,
  otherInformation,
  menuBackgroundColorClass: menuBackgroundColor,
  shouldShowTastingNotesHeader,
}: MenuProps) {
  const topRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<Map<string, HTMLDivElement> | null>(null);
  const [openInstagramModal, setOpenInstagramModal] = useState(false);
  const [instagramModalUrl, setInstagramModalUrl] = useState<string>("");

  const backgroundColorClass = menuBackgroundColor
    ? menuBackgroundColor
    : "bg-menu-bg"; // default dark blue

  const getMap = () => {
    if (!itemsRef.current) {
      itemsRef.current = new Map();
    }
    return itemsRef.current;
  };

  const scrollToFlavor = (id: string) => {
    const map = getMap();
    const node = map.get(id);
    if (node)
      node.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
  };

  // count total flavors
  const totalFlavors = flavors
    .map((f) => f.items.length)
    .reduceRight((p, c) => p + c);

  const onOpenInstagramModal = (instagramUrl: string) => {
    // set target instagram url
    setInstagramModalUrl(instagramUrl);
    setOpenInstagramModal(true);
  };
  const onCloseInstagramModal = () => setOpenInstagramModal(false);

  const getInstagramReadMoreButton = (instagramUrl: string) => {
    return (
      <div>
        <button
          className={
            "mt-2 font-ibm-plex-thai text-sm font-bold leading-[18px] whitespace-pre-wrap text-white inline-flex items-center" +
            `
                py-2 px-6           <!-- Padding (vertical and horizontal) -->
                rounded-lg          <!-- Rounded corners (user requested sm, but lg looks better) -->
                shadow-lg           <!-- Drop shadow for depth -->
                bg-gradient-to-r    <!-- Gradient direction: right -->
                from-yellow-500     <!-- Starting color -->
                via-pink-500        <!-- Middle color -->
                to-purple-600       <!-- Ending color -->
            `
          }
          onClick={() => onOpenInstagramModal(instagramUrl)}
        >
          อ่านเรื่องราวต่อใน &nbsp;
          <SocialLogo icon="instagram" size={20} fill="white" />
        </button>
      </div>
    );
  };

  return (
    <div className="">
      <div
        className="fixed bottom-10 right-10"
        onClick={() => {
          if (topRef.current)
            topRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
        }}
      >
        <img
          src={window.location.origin + "/flavorpedia/images/menu/up-arrow.svg"}
          width={"40px"}
        />
      </div>
      <Modal
        open={openInstagramModal}
        onClose={onCloseInstagramModal}
        modalId="customInstagramModal"
        center
      >
        <InstagramEmbed url={instagramModalUrl} className="mt-8" captioned />
      </Modal>
      <div
        ref={topRef}
        className={`${backgroundColorClass} py-8 flex flex-col items-center text-white`}
      >
        <div className="mb-6">
          <img
            src={window.location.origin + "/flavorpedia/images/menu/logo.svg"}
            style={{ height: "150px" }}
          />
        </div>
        {otherInformation ? (
          <>
            {/* Event name */}
            <div className="menu-header font-benaco-one mb-4 pl-6 pr-6 w-fit text-center font-normal text-key-header leading-key-header whitespace-pre-line">
              {headerText.split("<br>").join("\n")}
            </div>
            {/* Event date */}
            {otherInformation?.eventDate ? (
              <div className="menu-header font-benaco-one pl-6 pr-6 w-fit text-center font-normal text-sub-header leading-sub-header whitespace-pre-line">
                {otherInformation.eventDate}
              </div>
            ) : null}
            {/* Event location */}
            {otherInformation.eventLocation ? (
              <div className="menu-header font-benaco-one mb-8 pl-6 pr-6 w-fit text-center font-normal text-sub-header leading-sub-header whitespace-pre-line">
                {"".concat("@", otherInformation.eventLocation)}
              </div>
            ) : null}
          </>
        ) : (
          <div className="menu-header font-benaco-one mb-8 pl-6 pr-6 w-fit text-center font-normal text-key-header leading-key-header whitespace-pre-line">
            {/* Event name */}
            {headerText.split("<br>").join("\n")}
          </div>
        )}
        <div className="flex flex-col items-center mb-8 pl-6 pr-6">
          <div className="font-ibm-plex-thai text-lg mb-4 text-center">
            {"เราขอแนะนำไอศกรีมทั้งหมด"}
          </div>
          <div className="text-7xl font-benaco-one mb-4 w-fit text-center font-normal leading-10 whitespace-pre-line">
            {totalFlavors}
          </div>
          <div className="font-ibm-plex-thai text-lg">{"รสชาติ"}</div>
        </div>
        <div className="font-gt-super-text-bold text-lg mb-6">Menu</div>
        {flavors.map((flavor) => {
          return (
            <button
              key={`${flavor.id}-category-button`}
              onClick={() => scrollToFlavor(flavor.id)}
              className="font-ibm-plex-thai border-solid border border-white text-white mb-6 last:mb-0 px-4 py-2 w-fit text-lg leading-5 rounded-3xl"
            >
              {flavor.categoryButtonText}
            </button>
          );
        })}
        <div>
          <img
            src={
              window.location.origin + "/flavorpedia/images/menu/down-arrow.svg"
            }
            height={"27px"}
          />
        </div>
      </div>
      {flavors.map((flavor) => {
        const textColorClassName = flavor.textColorClass
          ? flavor.textColorClass
          : "text-white";
        return (
          <div
            ref={(node) => {
              const map = getMap();
              if (node) {
                map.set(flavor.id, node);
              } else {
                map.delete(flavor.id);
              }
            }}
            key={flavor.name}
            className={`py-10 px-6 flex flex-col items-center text-center gap-y-6 ${flavor.backgroundColorClass} ${textColorClassName}`}
          >
            {/* Cup image */}
            <img
              src={`${window.location.origin}/flavorpedia/images/menu/cups/${flavor.imageName}.png`}
              width={"240px"}
            />
            {/* Flavor name */}
            <div className="font-ibm-plex-thai text-2xl font-semibold leading-7">
              {flavor.name}
            </div>
            {/* Menu items */}
            {flavor.items.map((item) => {
              return (
                <div key={item.name} className="flex flex-col">
                  <div className="mb-1">
                    <span className="font-gt-super-text-bold text-lg leading-5 mb-2 italic">
                      {item.name}
                    </span>
                    {item.isSoldOut ? (
                      <span className="rounded-md w-auto ml-2 bg-red-900 text-slate-50 px-2 py-1 text-xs whitespace-nowrap shadow-lg">
                        {item.isSoldOut === SOLDOUT_TYPE.SOLDOUT
                          ? "Sold out"
                          : item.isSoldOut === SOLDOUT_TYPE.RESTOCKING_SOON
                            ? "Restocking soon"
                            : ""}
                      </span>
                    ) : item.isNewFlavor ? (
                      <span className="rounded-md w-auto ml-2 bg-slate-200 text-slate-800 px-2 py-1 text-xs whitespace-nowrap shadow-lg">
                        {"New"}
                      </span>
                    ) : undefined}
                  </div>
                  <div className="mb-1 font-ibm-plex-thai text-sm font-bold leading-[18px]">
                    <div>{item.price} บาท</div>
                    {/* Pre-order price */}
                    {item.preorderPrice ? (
                      <div>{`(Pre-order ก่อนวันงาน: ${item.preorderPrice}.-)`}</div>
                    ) : null}
                  </div>
                  <div className="font-ibm-plex-thai text-sm font-normal leading-[18px] whitespace-pre-wrap">
                    {item.description}
                  </div>
                  {/* Taste note */}
                  {item.tastingNotes ? (
                    <div className="mt-1 font-ibm-plex-thai text-sm font-bold leading-[18px] whitespace-pre-wrap">
                      {shouldShowTastingNotesHeader
                        ? `Tasting notes: ${item.tastingNotes}`
                        : item.tastingNotes}
                    </div>
                  ) : null}
                  {/* Instagram Url */}
                  {item.instagramUrl
                    ? getInstagramReadMoreButton(item.instagramUrl)
                    : null}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
